/**
 * useCustomerPick — turn a CustSrch result row into a fully-hydrated RaoPerson.
 *
 * Single source of the "officer picked an existing CIF" hydration path. Used by
 *   - PersonSearchBar (officer clicks a row in GlobalCustomerSearch), and
 *   - PeopleStep's no-dup-CIF guard (officer picks a match the silent CustSrch
 *     surfaced for a would-be-new person).
 *
 * Both must hydrate IDENTICALLY, so the CustInq fetch + `useInquiryHydration`
 * mapping lives here once. Re-inlining this mapping is the exact bug Bill
 * flagged 10+ times (2026-04-28): the prior caller cherry-picked a subset of
 * CustInq fields and dropped Gender / Employment / the whole CIP block.
 * `useInquiryHydration` covers every ParsedCustInq field — keep all callers on it.
 */
import { useCallback } from 'react';
import type { CustomerSearchRow } from '@/components/primitives';
import { useApi } from './useApi';
import { autoDetectMinor } from '../utils/age';
import { useInquiryHydration } from '../components/personDialog/shared/useInquiryHydration';
import type { RaoPerson, RaoPersonRole } from '../types';

/** Hydrate a RaoPerson shell from a CustSrch result row (pre-CustInq). */
export function personFromRow(row: CustomerSearchRow, role: RaoPersonRole): RaoPerson {
  return {
    role,
    mode: 'existing',
    firstName: row.firstName,
    lastName: row.lastName,
    fullName: row.name || `${row.firstName} ${row.lastName}`.trim(),
    cifId: row.cifId,
    taxId: row.taxId,
    customerType: row.custType === 'Business' ? 'Corporation' : 'Individual',
    cipStatus: 'pending',
    ...(row.phone ? { phoneCell: row.phone } : {}),
  };
}

export interface UseCustomerPickResult {
  /**
   * Fetch CustInq for the row's CIF and return a fully-populated RaoPerson.
   * On CustInq failure the search-row shell is returned (best-effort) so the
   * officer can still review-add with the partial record + correct manually.
   */
  pick: (row: CustomerSearchRow, role?: RaoPersonRole) => Promise<RaoPerson>;
}

export function useCustomerPick(): UseCustomerPickResult {
  const { client, makeSignal } = useApi();
  const { hydrate } = useInquiryHydration();

  const pick = useCallback(
    async (row: CustomerSearchRow, role: RaoPersonRole = 'primary'): Promise<RaoPerson> => {
      const shell = personFromRow(row, role);
      let draft: RaoPerson = shell;

      const signal = makeSignal();
      try {
        // includeUdf: the physical address of a customer whose mailing address
        // differs lives in UDF 86-89 (x_BusDetail), which costs a second Core
        // call — same reason RCM asks for it. Without it a Core-born customer
        // with a PO Box would hydrate with the PO Box as their physical address.
        const env = await client.accounts.custInq(row.cifId, { signal }, true);
        // Pass the inner `data` (camelCase wire) so the hook's
        // parseCustInq(input.data, ...) sees the unwrapped {CustRec, ...} shape.
        const hydrated = hydrate({
          data: (env as { data?: unknown; Data?: unknown }).data
             ?? (env as { Data?: unknown }).Data
             ?? env,
          cifId: row.cifId,
        });

        const detectedType = autoDetectMinor<string>(
          hydrated.customerType || shell.customerType || 'Individual',
          hydrated.dob || shell.dob,
        );
        draft = {
          ...shell,
          ...hydrated,
          // Hook hard-codes role='primary'; keep the caller's role so a joint-flow
          // pick doesn't get auto-promoted to primary.
          role,
          // CustSrch taxId may be masked vs CustInq's full; prefer hydrated.
          taxId: hydrated.taxId || shell.taxId,
          customerType: detectedType as RaoPerson['customerType'],
        };
      } catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          // eslint-disable-next-line no-console
          console.warn('[useCustomerPick] CustInq hydrate failed — returning search-row shell', err);
        }
      }
      return draft;
    },
    [client, makeSignal, hydrate],
  );

  return { pick };
}
